import React, { useEffect, useState } from 'react';
import { DataService } from '../services/dataService';
import { MenuItem, PageView } from '../types';
import { MapPin, Phone, Mail, Quote, Bean, ChefHat, Droplets, Heart } from 'lucide-react';

type CafeView = 'home' | 'menu' | 'about' | 'gallery';

interface CafePageProps {
  onNavigate: (page: PageView) => void;
}

export const CafePage: React.FC<CafePageProps> = ({ onNavigate }) => {
  const [view, setView] = useState<CafeView>('home');
  const [menu, setMenu] = useState<MenuItem[]>([]);

  useEffect(() => {
    setMenu(DataService.getMenu());
  }, []);

  // Update categories for Modern Brunch feel
  const categories = ['coffee', 'snack', 'dessert', 'drink'];
  const categoryNames: Record<string, string> = {
    coffee: 'Selection Coffee & Bar',
    snack: 'Brunch & Bistro',
    dessert: 'Bakery & Sweets',
    drink: 'Soft Drinks & Wine'
  };

  const galleryImages = [
    "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&q=80&w=600", // Barista pouring
    "https://images.unsplash.com/photo-1600093463592-8e36ae95ef56?auto=format&fit=crop&q=80&w=600", // Modern Food
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&q=80&w=600", // Interior Details
    "https://images.unsplash.com/photo-1442512595367-427325097196?auto=format&fit=crop&q=80&w=600", // Coffee Machine
    "https://images.unsplash.com/photo-1579952777085-0210e75a7c29?auto=format&fit=crop&q=80&w=600", // Plated Dessert
    "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&q=80&w=600", // Coffee Beans
  ];

  return (
    <div className="min-h-screen bg-[#fdfbf7] text-[#2c2c2c] font-sans flex flex-col">
      
      {/* Sub-Navigation - Minimalist Modern */}
      <div className="bg-white/90 border-b border-gray-100 sticky top-20 z-40 py-4 shadow-sm backdrop-blur-md">
        <div className="max-w-5xl mx-auto flex justify-center flex-wrap gap-2 md:gap-8 text-sm uppercase tracking-widest font-bold text-gray-500">
            <button onClick={() => setView('home')} className={`px-4 py-2 hover:text-cafe-accent transition-colors ${view === 'home' ? 'text-cafe-accent border-b-2 border-cafe-accent' : ''}`}>Úvod</button>
            <button onClick={() => setView('menu')} className={`px-4 py-2 hover:text-cafe-accent transition-colors ${view === 'menu' ? 'text-cafe-accent border-b-2 border-cafe-accent' : ''}`}>Menu & Brunch</button>
            <button onClick={() => setView('about')} className={`px-4 py-2 hover:text-cafe-accent transition-colors ${view === 'about' ? 'text-cafe-accent border-b-2 border-cafe-accent' : ''}`}>O nás</button>
            <button onClick={() => setView('gallery')} className={`px-4 py-2 hover:text-cafe-accent transition-colors ${view === 'gallery' ? 'text-cafe-accent border-b-2 border-cafe-accent' : ''}`}>Galerie</button>
            <button onClick={() => onNavigate('RESERVATION')} className="px-4 py-2 hover:text-cafe-accent transition-colors text-guild-gold font-bold">Rezervace</button>
        </div>
      </div>

      <div className="animate-fade-in flex-grow">
        
        {/* VIEW: HOME */}
        {view === 'home' && (
            <>
                {/* Hero Section - Cleaner, Modern */}
                <header className="relative h-[80vh] flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-gray-900/30 z-10"></div>
                    <img 
                        src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&q=80&w=1920" 
                        alt="Coffee Shop Interior" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    
                    <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
                        <span className="bg-white/90 text-cafe-accent px-4 py-1 uppercase tracking-[0.3em] text-xs font-bold mb-6 inline-block backdrop-blur">
                            Specialty Coffee & Bistro
                        </span>
                        <h1 className="text-6xl md:text-8xl font-serif font-bold text-white mb-6 drop-shadow-lg leading-tight">
                            Kavárna <br/> <span className="italic font-light">na náměstí</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 font-light mb-10 max-w-2xl mx-auto font-serif italic">
                            Výběrová káva, domácí dezerty a moderní gastronomie v srdci Bruntálu.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center gap-4">
                            <button onClick={() => setView('menu')} className="bg-white text-gray-900 px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-cafe-accent hover:text-white transition-all duration-300">
                                Prohlédnout Menu
                            </button>
                            <button onClick={() => onNavigate('RESERVATION')} className="bg-transparent border-2 border-white text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-gray-900 transition-all duration-300">
                                Rezervovat Stůl
                            </button>
                        </div>
                    </div>
                </header>

                {/* Highlights - "PS Coffee" style */}
                <div className="max-w-7xl mx-auto px-6 py-24">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">Proč k nám?</h2>
                        <div className="w-16 h-1 bg-cafe-accent mx-auto"></div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12">
                        <div className="text-center group p-6 hover:bg-white hover:shadow-xl transition-all duration-500 rounded-sm">
                            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-cafe-accent transition-colors duration-500">
                                <Bean className="w-10 h-10 text-gray-600 group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="text-xl font-bold uppercase tracking-wide mb-3">Výběrová Káva</h3>
                            <p className="text-gray-600 leading-relaxed font-serif">
                                Spolupracujeme s předními evropskými pražírnami. Pravidelně střídáme zrna na mlýnku, abyste mohli objevovat nové chutě z celého světa.
                            </p>
                        </div>

                        <div className="text-center group p-6 hover:bg-white hover:shadow-xl transition-all duration-500 rounded-sm">
                            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-cafe-accent transition-colors duration-500">
                                <ChefHat className="w-10 h-10 text-gray-600 group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="text-xl font-bold uppercase tracking-wide mb-3">Snídaně & Brunch</h3>
                            <p className="text-gray-600 leading-relaxed font-serif">
                                Od avokádových toastů po nadýchané lívance. Používáme vajíčka z volného chovu a kváskový chléb z místní pekárny.
                            </p>
                        </div>

                        <div className="text-center group p-6 hover:bg-white hover:shadow-xl transition-all duration-500 rounded-sm">
                            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-cafe-accent transition-colors duration-500">
                                <Heart className="w-10 h-10 text-gray-600 group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="text-xl font-bold uppercase tracking-wide mb-3">Domácí Dezerty</h3>
                            <p className="text-gray-600 leading-relaxed font-serif">
                                Žádné polotovary. Všechny dorty, koláče i sušenky pečeme s láskou přímo u nás v kuchyni. Čerstvost je pro nás zákon.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Banner Section */}
                <div className="relative h-96 bg-fixed bg-center bg-cover flex items-center justify-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1511920170033-f8396924c348?auto=format&fit=crop&q=80&w=1920')"}}>
                    <div className="absolute inset-0 bg-black/50"></div>
                    <div className="relative z-10 text-center text-white px-4">
                        <Quote className="w-12 h-12 mx-auto mb-6 text-cafe-accent opacity-80" />
                        <h3 className="text-3xl md:text-5xl font-serif font-bold italic mb-6">"Káva je ovoce."</h3>
                        <p className="uppercase tracking-widest text-sm font-bold opacity-80">Přijďte ochutnat rozdíl</p>
                    </div>
                </div>
            </>
        )}

        {/* VIEW: MENU */}
        {view === 'menu' && (
            <div className="max-w-5xl mx-auto px-6 py-16">
                <div className="text-center mb-20">
                    <span className="text-cafe-accent uppercase tracking-widest font-bold text-xs block mb-2">Gastronomie</span>
                    <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Menu</h2>
                    <p className="text-gray-500 max-w-2xl mx-auto font-serif italic text-lg">
                        Kuchyně otevřena denně do 15:00. Káva a dezerty až do zavíračky.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-16">
                    {categories.map(cat => {
                        const items = menu.filter(m => m.category === cat);
                        if (items.length === 0) return null;

                        return (
                            <div key={cat} className="bg-white p-8 shadow-sm border border-gray-100 rounded-sm">
                                <h3 className="text-2xl font-bold uppercase tracking-wider mb-8 border-b-2 border-cafe-accent pb-2 inline-block text-gray-800">
                                    {categoryNames[cat] || cat}
                                </h3>
                                <div className="space-y-8">
                                    {items.map(item => (
                                        <div key={item.id} className="flex gap-4 group">
                                            {item.image && (
                                                <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                                </div>
                                            )}
                                            <div className="flex-grow">
                                                <div className="flex justify-between items-baseline mb-1">
                                                    <h4 className="text-lg font-bold text-gray-800 group-hover:text-cafe-accent transition-colors">{item.name}</h4>
                                                    <span className="font-bold text-cafe-accent ml-4">{item.price} Kč</span>
                                                </div>
                                                <p className="text-gray-500 text-sm font-serif leading-relaxed">{item.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        )}

        {/* VIEW: ABOUT / PHILOSOPHY */}
        {view === 'about' && (
            <div className="max-w-6xl mx-auto px-6 py-16">
                <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
                    <div className="order-2 md:order-1">
                        <span className="text-cafe-accent uppercase tracking-widest font-bold text-xs block mb-2">O nás</span>
                        <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">Filosofie výběrové kávy</h2>
                        <p className="text-gray-600 mb-6 leading-relaxed text-lg font-serif">
                            Nejsme pražírna, jsme milovníci kvality. Věříme, že připravit dokonalou kávu je umění, které začíná výběrem správných zrn.
                        </p>
                        <p className="text-gray-600 mb-8 leading-relaxed text-lg font-serif">
                            Proto pro vás pravidelně vybíráme to nejzajímavější z nabídky renomovaných českých i zahraničních pražíren. 
                            Ať už máte rádi ovocné a lehké filtry, nebo plné a čokoládové espresso, u nás si přijdete na své.
                            Naši baristé se neustále vzdělávají, aby z každého zrnka dostali maximum.
                        </p>
                        <ul className="space-y-4 font-bold text-gray-800 uppercase tracking-wider text-sm">
                            <li className="flex items-center gap-2"><div className="w-2 h-2 bg-cafe-accent rounded-full"></div> Výběr top pražíren</li>
                            <li className="flex items-center gap-2"><div className="w-2 h-2 bg-cafe-accent rounded-full"></div> Domácí pečení</li>
                            <li className="flex items-center gap-2"><div className="w-2 h-2 bg-cafe-accent rounded-full"></div> Profesionální Baristé</li>
                        </ul>
                    </div>
                    <div className="order-1 md:order-2 relative h-[500px]">
                        {/* New image focusing on Preparation/Barista instead of Roasting */}
                        <img src="https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=800" alt="Barista Preparation" className="absolute inset-0 w-full h-full object-cover shadow-2xl rounded-sm" />
                        <div className="absolute -bottom-8 -left-8 bg-white p-6 shadow-xl max-w-xs hidden md:block">
                            <p className="font-serif italic text-gray-600">"Každý šálek je pro nás příležitost zlepšit vám den."</p>
                            <p className="text-xs font-bold uppercase mt-2 text-cafe-accent">— Tým Kavárny</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-900 text-white p-12 rounded-sm text-center">
                    <h3 className="text-3xl font-serif font-bold mb-4">Příslušenství a zrnková káva</h3>
                    <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                        Chutnala vám káva u nás? Balíčky čerstvě pražených zrn od našich dodavatelů i vybavení pro domácí přípravu (Hario, Aeropress) zakoupíte přímo na baru.
                    </p>
                    <button className="bg-cafe-accent text-white px-8 py-3 uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">
                        Zobrazit nabídku na baru
                    </button>
                </div>
            </div>
        )}

         {/* VIEW: GALLERY */}
         {view === 'gallery' && (
            <div className="max-w-7xl mx-auto px-6 py-16">
                 <div className="text-center mb-16">
                    <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">Život v kavárně</h2>
                    <p className="text-gray-500 font-serif italic">Momentky z každodenního shonu i klidných rán.</p>
                 </div>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                     {galleryImages.map((src, i) => (
                         <div key={i} className="aspect-square relative group overflow-hidden bg-gray-100">
                             <img src={src} alt="Galerie" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                             <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <span className="text-white uppercase tracking-widest font-bold text-sm border border-white px-4 py-2">Zvětšit</span>
                             </div>
                         </div>
                     ))}
                 </div>
            </div>
        )}

      </div>
      
      {/* Footer - Minimalist */}
      <footer className="bg-white text-gray-900 border-t border-gray-100 py-16">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-12">
              <div className="col-span-1 md:col-span-2">
                  <h3 className="font-serif font-bold text-2xl mb-6">Kavárna na náměstí</h3>
                  <p className="text-gray-500 leading-relaxed font-serif max-w-xs">
                      Výběrová káva, domácí dorty a moderní brunch v historickém centru Bruntálu. 
                      Jsme místo, kde se setkává tradice s moderní gastronomií.
                  </p>
              </div>
              <div>
                  <h4 className="font-bold mb-6 uppercase tracking-widest text-xs text-gray-400">Kontakt</h4>
                  <ul className="space-y-3 text-sm text-gray-600">
                      <li className="flex items-center gap-3"><MapPin size={16} /> Náměstí Míru 15, Bruntál</li>
                      <li className="flex items-center gap-3"><Phone size={16} /> +420 777 123 456</li>
                      <li className="flex items-center gap-3"><Mail size={16} /> hello@kavarnananamesti.cz</li>
                  </ul>
              </div>
              <div>
                  <h4 className="font-bold mb-6 uppercase tracking-widest text-xs text-gray-400">Otevírací doba</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex justify-between"><span>Po - Pá</span> <span className="font-bold">08:00 - 20:00</span></li>
                    <li className="flex justify-between"><span>So</span> <span className="font-bold">09:00 - 21:00</span></li>
                    <li className="flex justify-between"><span>Ne</span> <span className="font-bold">09:00 - 18:00</span></li>
                  </ul>
              </div>
          </div>
          <div className="text-center mt-16 pt-8 border-t border-gray-100 text-xs text-gray-400 uppercase tracking-widest">
              © 2023 Kavárna na náměstí • Created with coffee & love
          </div>
      </footer>
    </div>
  );
};